'use client';

/*
  COMPONENT LOGIC
  ***********************************************
  Case 1: not authenticated
  1. If the form validates, it will be submitted with 'handleStep'.
  2. We get the data from 'formStatus' state and save it in sessionStorage.
  3. We send the user to login, then redirect to the cv-worth page.

  Case 2: authenticated
  1. We check the session storage for saved data. If any, we take it and fill the form, then
  auto-submit the form.
  2. If no data in session storage, we make an API call for the last experience and fill the form.
  3. We save the data from the API call to Redux state (state.lastWorkExperience). This way, on revisit
  we take the data from the store and avoid unneeded API calls that would return the same data.
 */


// Imports
// ***********************************************
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useAppDispatch, useAppSelector } from 'src/app';
import { useBjForm } from 'src/hooks';
import {
  Input, ComboBox, Button, Label, RadioText,
} from '@components/common/Forms';
import {
  getObjectFromSessionStorage, isSuggestedSalaryOk,
  ApiJobTitleAutocompleteOk, ApiLocationTooltipOk, ApiSuggestedSalary,
} from 'src/utils';
import { FlattenedCvWorthForm } from 'src/types/cv-worth';


// Dynamic imports
// ***********************************************
const DynamicCvWorthHelperText = dynamic(() => import('./Helper-text')
  .then((mod) => mod.CvWorthHelperText), { ssr: false });

interface IProps {
  isAuth?: boolean | Promise<boolean>,
  showHelperText?: boolean,
  compact?: boolean
  isSalaryContext?: boolean
}

// Main component
// 1. Auth prop
// 2. If we want to show the helper text: 'you must be logged...'
// 3. Compact mode for homepage
// ***********************************************
export const CvWorthForm = ({
  isAuth = false, showHelperText = true, compact = false, isSalaryContext = false,
}: IProps) => {
  // Translation, redux dispatch, router, ref for form
  const { t } = useTranslation('common');
  const appDispatch = useAppDispatch();
  const router = useRouter();
  const formRef = useRef(null);


  // **************************
  // 1. Session storage values.
  const getStorageValue = useMemo(() => (typeof window !== 'undefined' ? getObjectFromSessionStorage('cv-worth-data') : {}), []);
  const storageValue = useMemo(() => (Object.keys(getStorageValue).length > 0 ? getStorageValue : null), [getStorageValue]);

  // 2. Redux values. Saved after calling the last-work-experience endpoint.
  const reduxLastWorkExperience = useAppSelector((state) => state.user.lastWorkExperience);

  // 3. Config for 'withDefaultValues' option for fetching the last user experience from API
  const fetchExperience = useMemo(() => [{
    reqURL: '/v1/resume/last-work-experience',
    putInKey: 'experienceWrite',
  }], []);

  // Determine the value for the 'withDefaultValues' option of useBjForm when authenticated.
  // 1. Values from storage. 2. Values from Redux state. 3. Config for fetch data.
  const formDefaultValues = useMemo(
    () => (isAuth ? (storageValue || reduxLastWorkExperience || fetchExperience) : null),
    [isAuth, storageValue, reduxLastWorkExperience, fetchExperience],
  );


  // BjForm Library
  // **************************
  const {
    handleSubmit, handleStep, record, formStatus, getValuesStatus, isPosting, setFormValues, getFieldValue,
  } = useBjForm({
    submitURL: '/v1/resume-operation/resume/suggested-salary',
    withDefaultValues: formDefaultValues,
    scrollToFirstError: false,
    showToasts: { ignoreStatus: ['200', '201', '400'] },
  });


  // Main logic
  // **************************
  useEffect(() => {
    // Avoid ref changing on every render
    const form = formRef.current !== null ? formRef.current as unknown as HTMLFormElement : null;

    // 1. On submit, save the form in session storage if user is not authenticated.
    // Then, redirect the user back to the cv-worth page after login.
    if (!isAuth && formStatus.actionStatus === 'success') {
      sessionStorage.setItem('cv-worth-data', JSON.stringify(formStatus.form));
      // we're not redirecting inside React, so no router.push
      window.location.href = '/login?cv-worth=1';
    }

    // 2. Auto-submit the form if we have values from session storage.
    // Timeout to give some room for render to finish. Also check at least one field to have a value.
    const autoSubmitFromStorage = setTimeout(() => {
      if (storageValue && isAuth && sessionStorage.getItem('cv-worth-data') && form !== null && getFieldValue('experienceWrite.title')) {
        sessionStorage.removeItem('cv-worth-data');
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }, 50);

    // 3. Handle a successful post response => redirect to stats page.
    if (isAuth && formStatus.actionStatus === 'success') {
      const successData = formStatus.data as ApiSuggestedSalary;
      if (isSuggestedSalaryOk(successData) && successData.result !== undefined) {
        const query = isSalaryContext ? 'isSalaryContext=1' : '';
        void router.push(successData.result.url + (successData.result.url?.includes('?') ? '&' : '?') + query);
      }
    }

    // Cleanups
    return () => {
      clearTimeout(autoSubmitFromStorage);
    };
  }, [isAuth, formStatus, storageValue, router, getFieldValue, isSalaryContext]);


  // Save the last user experience that we got from API to redux, in order to reduce the number of API calls.
  // Also, take care of the mismatch between nested object for salary in APIs
  // (suggested-salary: salary.data.amount vs last-work-experience: salary.amount)
  // **************************
  useEffect(() => {
    if (getValuesStatus.actionStatus === 'success' && getValuesStatus.form !== undefined) {
      const lastExperience = getValuesStatus.form as FlattenedCvWorthForm;
      let validFormValues = lastExperience.experienceWrite;

      // Check if we have a salary in proper currency and assign to required key
      if (
        Object.hasOwn(validFormValues, 'experienceWrite.salary.amount')
        && Object.hasOwn(validFormValues, 'experienceWrite.salary.currency')
        && validFormValues['experienceWrite.salary.currency'] === 'EUR'
      ) {
        validFormValues = {
          ...validFormValues,
          'experienceWrite.salary.data.amount': validFormValues['experienceWrite.salary.amount'],
        };
        setFormValues({ 'experienceWrite.salary.data.amount': validFormValues['experienceWrite.salary.data.amount'] });
      }

      appDispatch({ type: 'user/saveLastWorkExperience', payload: validFormValues });
    }
  }, [getValuesStatus, appDispatch, setFormValues]);


  // Render the form
  // **************************
  return (
    <form className={`grid ${compact ? 'grid-cols-2 gap-x-3 md:gap-x-4 lg:-ml-5 lg:bg-surface/50 lg:p-6' : 'grid-cols-1 md:grid-cols-2 md:gap-x-4'}`} noValidate onSubmit={isAuth ? handleSubmit : handleStep} ref={formRef}>

      {/* Job TITLE autocomplete ****************************************** */}
      <Label
        className={compact ? 'col-span-2 lg:mt-0' : 'md:col-span-2'}
        description={t('global.job.title')}
        descriptionHelper={compact ? '' : `(${t('form.validity.min-chars-3')})`}
        descriptionClass={`font-bold !mb-2 ${compact ? 'text-dark' : 'text-ink-medium'}`}
        required
        as="div"
      >
        <ComboBox
          required
          minLength={3}
          placeholder={t('cv-value-label-current-job-placeholder')}
          options={{
            valueFromKey: 'name',
            apiList: {
              fetchUrl: '/job-title-autocomplete',
              queryParams: (query: string) => ({ term: query }),
            },
          }}
          renderList={(item, index, listKey, getItemProps, listStyling, highlightedIndex) => {
            // add a type to make TS behave nicely; we already checked for errors object inside Combobox fetch
            const job = item as ApiJobTitleAutocompleteOk;
            return (
              <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                <span>{job.name}</span>
                <span className={`${highlightedIndex === index ? 'text-white' : 'text-ink-medium'} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                  { // we might have multiple domains for a job name, spread them nicely
                    job.domains && (job.domains).map((domain, i) => {
                      if (i === 0) return (domain.name);
                      return (`, ${domain.name}`);
                    })
                  }
                </span>
              </li>
            );
          }}
          {...record('experienceWrite.title')}
        />
      </Label>


      {/* Job LOCATION autocomplete; must be BOX type ************************************** */}
      <Label
        className={compact ? '!mt-2 md:!mt-4' : ''}
        description={t('cv-value-label-city')}
        descriptionHelper={compact ? '' : `(${t('global.combobox.box-type')}, ${t('form.validity.min-chars-3')})`}
        descriptionClass={`font-bold !mb-2 text-nowrap ${compact ? 'text-dark' : 'text-ink-medium'}`}
        required
        as="div"
      >
        <ComboBox
          required
          minLength={3}
          placeholder={t('cv-value-label-city-placeholder')}
          options={{
            freeSolo: false,
            valueFromKey: 'locality',
            apiList: {
              fetchUrl: '/v1/location-tooltip',
              queryParams: (query: string) => ({ location: query }),
            },
          }}
          renderList={(item, index, listKey, getItemProps, listStyling, highlightedIndex) => {
            // add a type to make TS behave nicely; we already checked for errors object inside Combobox fetch
            const location = item as ApiLocationTooltipOk;
            return (
              <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                <span>{location.locality}</span>
                <span className={`${highlightedIndex === index ? 'text-white' : 'text-ink-medium'} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                  {location.country}
                </span>
              </li>
            );
          }}
          {...record('experienceWrite.location')}
        />
      </Label>


      {/* Job SALARY input ****************************************** */}
      <Label
        className={compact ? '!mt-2 md:!mt-4' : ''}
        htmlFor="experienceWrite.salary.data.amount"
        description={t('salary.landing.actual_salary')}
        descriptionClass={`font-bold !mb-2 ${compact ? 'text-dark' : 'text-ink-medium'}`}
        required
      >
        <Input
          type="number"
          min={350}
          required
          placeholder={t('cv-value-label-salary-placeholder')}
          {...record('experienceWrite.salary.data.amount')}
        />
      </Label>


      {/* HIDDEN INPUTS with the extra fields needed for the 'salary' nested object  */}
      <div className="hidden md:col-span-2">
        <Input type="hidden" readOnly {...record('experienceWrite.salary.data.currency', { defaultValue: 'EUR' })} />
        <Input type="hidden" readOnly {...record('experienceWrite.salary.data.confidential', { defaultValue: 'true', convertValue: 'toBoolean' })} />
      </div>


      {/* Job SAVE TO PROFILE checkbox & SUBMIT button **************************************** */}
      <div className={`grid md:mt-10 md:grid-cols-2 md:items-center md:gap-x-4 ${compact ? 'col-span-2 lg:mt-6' : 'md:col-span-2'}`}>
        <div className={`hidden pr-3 text-right md:order-last md:mb-0 md:mt-0.5 ${compact ? 'my-3' : 'my-6'}`}>
          <Label htmlFor="experienceSave" className="!mt-0 !inline-flex !w-auto items-center">
            <RadioText className={`text-sm ${compact ? 'text-dark' : 'text-ink-medium'}`}>{t('cv-value-save-to-profile')}</RadioText>
            <Input
              type="checkbox"
              className="ml-2"
              {...record('experienceSave', {
                convertValue: 'toBoolean', convertEmptyValue: 'toBoolean', defaultValue: 'false', defaultChecked: false,
              })}
            />
          </Label>
        </div>
        <Button type="submit" rounding="full" color="secondary" className="mt-4 w-full !px-2" isLoading={isPosting}>
          {isPosting ? t('global.label.sending.data') : t('salary.landing.cta')}
        </Button>
      </div>

      {/* Helper text ****************************************** */
        showHelperText && (
          <p className="mt-5 text-center text-tiny text-ink-medium md:col-span-2 md:mb-1.5 md:mt-6 md:text-left">
            <DynamicCvWorthHelperText />
            <span>&nbsp;</span>
          </p>
        )
      }

    </form>
  );
};
